/**
 * RecurringRibbon — M-Rhythm-2 / M-Rhythm-3
 *
 * Compact horizontal ribbon ABOVE the calendar grid, one row per active
 * recurring cadence with HIGH frequency (M-Rhythm-2: just daily check-in;
 * design supports more cadence types in future milestones).
 *
 * M-Rhythm-3 additions:
 *   - "Skip this occurrence" button per row in the occurrences drawer
 *   - For virtual occurrences (id === null), materialize first then skip
 *   - Dimmed visual style for skipped rows
 *   - skip-with-reason modal
 *
 * Compliance: M-Rhythm-3 plan §5 task 9.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Textarea } from '@/components/ui/Textarea';
import { showToast } from '@/components/ui/Toast';
import { formatDate } from '@/lib/localeFormatters';
import { apiFetch } from '@/lib/api';
import { meetingKeys } from '@/hooks/roros/meetings';
import type { CalendarMeetingDTO, MeetingStatus } from '@/types/roros/meetings';

/** F2.2: typed API error shape — avoids `as any` casts on error responses. */
interface ApiErrorResponse { error?: { message?: string; code?: string } }
import { statusVariant, statusLabel } from './meeting-status-utils';

// ─── Status dot ───────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: MeetingStatus | null }) {
  const colorMap: Record<string, string> = {
    scheduled:   'bg-primary-400',
    in_progress: 'bg-primary-600',
    completed:   'bg-success-500',
    skipped:     'bg-muted-foreground',
    cancelled:   'bg-error-400',
  };
  const color = status ? (colorMap[status] ?? 'bg-muted') : 'bg-muted/30';

  return (
    <span
      className={cn('inline-block h-2.5 w-2.5 rounded-full', color)}
      aria-label={status ?? t('No status today')}
      title={status ?? t('No status today')}
    />
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

// Group meetings by cadenceScheduleId to create per-cadence ribbon rows
interface RibbonRow {
  cadenceScheduleId: string;
  name: string;
  todayStatus: MeetingStatus | null;
  occurrences: CalendarMeetingDTO[]; // all occurrences in the loaded window
}

// ─── SkipReasonModal ──────────────────────────────────────────────────────────

interface SkipReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  isLoading: boolean;
}

function SkipReasonModal({ isOpen, onClose, onConfirm, isLoading }: SkipReasonModalProps) {
  const [reason, setReason] = useState('');

  const handleConfirm = async () => {
    await onConfirm(reason);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('Skip this occurrence')}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
            {t('Skip')}
          </Button>
        </div>
      }
    >
      <Textarea
        label={t('Reason (optional)')}
        placeholder={t('Why is this occurrence being skipped?')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={isLoading}
        rows={3}
      />
    </Modal>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface RecurringRibbonProps {
  /** All meetings in the current calendar window */
  meetings: CalendarMeetingDTO[];
  className?: string;
}

export function RecurringRibbon({ meetings, className }: RecurringRibbonProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openRow, setOpenRow] = useState<RibbonRow | null>(null);
  const [skipTarget, setSkipTarget] = useState<CalendarMeetingDTO | null>(null);
  const [isSkipping, setIsSkipping] = useState(false);

  // Group daily meetings by cadenceScheduleId
  const ribbonRows = useMemo<RibbonRow[]>(() => {
    const daily = meetings.filter((m) => m.meetingType === 'daily');
    const grouped = new Map<string, RibbonRow>();

    for (const m of daily) {
      const key = m.cadenceScheduleId ?? 'ad_hoc';
      if (!grouped.has(key)) {
        const todayMeeting = daily.find(
          (d) => d.cadenceScheduleId === key && isToday(d.scheduledAt),
        );
        grouped.set(key, {
          cadenceScheduleId: key,
          name: t('Daily Check-in'),
          todayStatus: (todayMeeting?.status as MeetingStatus) ?? null,
          occurrences: [],
        });
      }
      grouped.get(key)!.occurrences.push(m);
    }

    // Sort occurrences within each row
    for (const row of grouped.values()) {
      row.occurrences.sort(
        (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
      );
    }

    return [...grouped.values()];
  }, [meetings]);

  /**
   * Skip a meeting occurrence.
   * For virtual occurrences (id === null), materialize first then skip.
   */
  const handleSkip = async (occ: CalendarMeetingDTO, reason: string) => {
    setIsSkipping(true);
    try {
      let meetingId = occ.id;

      // If this is a virtual occurrence, materialize it first.
      // F1.2: Pass cadence_schedule_id so the server uses the idempotent
      // repo.materialize() path — two concurrent skips of the same virtual
      // occurrence safely converge to the same DB row.
      if (!meetingId && occ.cadenceScheduleId) {
        const materializeRes = await apiFetch('/api/v1/meetings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: occ.title,
            scheduled_at: occ.scheduledAt,
            duration_minutes: occ.durationMinutes,
            cadence_schedule_id: occ.cadenceScheduleId,
          }),
        });
        if (!materializeRes.ok) {
          const err = await materializeRes.json().catch((e: unknown) => {
            console.warn('[RecurringRibbon] Failed to parse materialize error response', e);
            return {} as ApiErrorResponse;
          });
          throw new Error(err.error?.message ?? t('Failed to create occurrence'));
        }
        const materializeData = await materializeRes.json() as { data?: { id?: string } };
        meetingId = materializeData.data?.id ?? null;
      }

      if (!meetingId) {
        throw new Error(t('Cannot skip: meeting ID not available'));
      }

      const skipRes = await apiFetch(`/api/v1/meetings/${meetingId}/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || undefined }),
      });

      if (!skipRes.ok) {
        const err = await skipRes.json().catch((e: unknown) => {
          console.warn('[RecurringRibbon] Failed to parse skip error response', e);
          return {} as ApiErrorResponse;
        });
        throw new Error((err as ApiErrorResponse).error?.message ?? t('Failed to skip meeting'));
      }

      showToast.success(t('Occurrence skipped'));
      setSkipTarget(null);
      setOpenRow(null);

      // Invalidate calendar queries to refresh the view
      await queryClient.invalidateQueries({ queryKey: meetingKeys.all });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('An error occurred');
      showToast.error(message);
    } finally {
      setIsSkipping(false);
    }
  };

  if (ribbonRows.length === 0) return null;

  return (
    <>
      <div
        className={cn(
          'rounded-lg border border-border bg-muted/30 px-4 py-2 mb-3',
          className,
        )}
        data-testid="recurring-ribbon"
        aria-label={t('Recurring meetings')}
      >
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          {t('Recurring meetings')}
        </p>
        <div className="flex flex-col gap-1">
          {ribbonRows.map((row) => (
            <Button
              key={row.cadenceScheduleId}
              variant="ghost"
              size="sm"
              onClick={() => setOpenRow(row)}
              className="flex items-center gap-3 text-sm text-left w-full justify-start h-auto px-2 py-1.5 hover:bg-muted/50"
              aria-label={`${row.name} — ${t('Next 14 occurrences')}`}
              data-testid={`ribbon-row-${row.cadenceScheduleId}`}
            >
              <StatusDot status={row.todayStatus} />
              <span className="font-medium">{row.name}</span>
              <span className="text-xs text-muted-foreground">Mo–Fr</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Occurrences drawer/modal */}
      {/* CB-007: Boilerplate Modal provides: role="dialog", aria-modal="true",
          Escape-key close, focus trapping (tabIndex={-1} on modal panel),
          and focus-return to triggering element on close. No extra aria attrs needed. */}
      {openRow && (
        <Modal
          isOpen={!!openRow}
          onClose={() => setOpenRow(null)}
          title={openRow.name}
          size="md"
          footer={
            <div className="flex items-center justify-between w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/roros/structure-settings?tab=cadence')}
              >
                {t('Edit cadence')}
              </Button>
              <Button variant="ghost" onClick={() => setOpenRow(null)}>
                {t('Close')}
              </Button>
            </div>
          }
        >
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground mb-3">
              {t('Next 14 occurrences')}
            </p>
            {openRow.occurrences
              .filter((occ) => new Date(occ.scheduledAt) >= (() => { const t = new Date(); t.setHours(0,0,0,0); return t; })())
              .slice(0, 14)
              .map((occ, idx) => {
              const isSkipped = occ.status === 'skipped';
              const isCancelled = occ.status === 'cancelled';
              const isDimmed = isSkipped || isCancelled;
              const canSkip = !isSkipped && !isCancelled && occ.status !== 'completed';

              return (
                <div
                  key={occ.id ?? `virtual-${idx}`}
                  className={cn(
                    'flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0',
                    isDimmed && 'opacity-50',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <StatusDot status={occ.status as MeetingStatus} />
                    <span className={cn(isDimmed && 'line-through')}>
                      {formatDate(new Date(occ.scheduledAt))}
                    </span>
                    {isToday(occ.scheduledAt) && (
                      <span className="text-xs text-primary-600 font-medium">{t('Today')}</span>
                    )}
                    {isSkipped && occ.skippedReason && (
                      <span className="text-xs text-muted-foreground italic truncate max-w-[120px]" title={occ.skippedReason}>
                        — {occ.skippedReason}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant(occ.status as MeetingStatus)}>
                      {statusLabel(occ.status as MeetingStatus)}
                    </Badge>
                    {canSkip && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-6 px-2 text-muted-foreground hover:text-destructive"
                        onClick={() => { setOpenRow(null); setSkipTarget(occ); }}
                        aria-label={t('Skip this occurrence')}
                        data-testid={`ribbon-skip-${occ.id ?? `virtual-${idx}`}`}
                      >
                        {t('Skip')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {openRow.occurrences.length === 0 && (
              <p className="text-muted-foreground text-xs">{t('No occurrences in this window')}</p>
            )}
          </div>
        </Modal>
      )}

      {/* Skip-with-reason modal */}
      {/* CB-007: Boilerplate Modal provides: role="dialog", aria-modal="true",
          Escape-key close, focus trapping (tabIndex={-1} on modal panel),
          and focus-return to triggering element on close. No extra aria attrs needed. */}
      {skipTarget && (
        <SkipReasonModal
          isOpen={!!skipTarget}
          onClose={() => setSkipTarget(null)}
          onConfirm={(reason) => handleSkip(skipTarget, reason)}
          isLoading={isSkipping}
        />
      )}
    </>
  );
}
